import React from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { formatFloat } from '@/utils/format';
import { Item } from '@/api/models/Item';

interface DataDeficiencyDialogProps {
  visible: boolean;
  onHide: () => void;
  items: Item[];
}

const DataDeficiencyDialog: React.FC<DataDeficiencyDialogProps> = ({
  visible,
  onHide,
  items
}) => {
  // データ不備の品目をフィルタ (データ不備配列が存在し要素があるもの)
  const deficiencyItems = items.filter(item => Array.isArray(item.データ不備) && item.データ不備.length > 0);

  // データ不備の理由を表示するテンプレート
  const deficiencyReasonTemplate = (rowData: Item) => {
    const reasons: string[] = Array.isArray(rowData.データ不備) ? rowData.データ不備 : [];

    return (
      <div className="flex flex-wrap gap-1">
        {reasons.map((reason, index) => (
          <Tag
            key={index}
            value={reason}
            severity="warning"
            className="text-xs"
          />
        ))}
      </div>
    );
  };

  // 在庫上限のテンプレート
  const stockMaxTemplate = (rowData: Item) => {
  const val = rowData.在庫上限?.value
  const reasons: string[] = Array.isArray(rowData.データ不備) ? rowData.データ不備 : [];
  const isDefault = (val === 100 && reasons.some(r => r.includes('在庫上限')))
  const display = val ?? '未設定'
  return <span className={isDefault ? 'text-red-600' : ''}>{display}</span>;
  };

  // 在庫下限のテンプレート
  const stockMinTemplate = (rowData: Item) => {
  const val = rowData.在庫下限?.value
  const reasons: string[] = Array.isArray(rowData.データ不備) ? rowData.データ不備 : [];
  const isDefault = (val === 0 && reasons.some(r => r.includes('在庫下限')))
  const display = val ?? '未設定'
  return <span className={isDefault ? 'text-red-600' : ''}>{display}</span>;
  };

  // 工程時間のテンプレート
  const processTimeTemplate = (rowData: Item) => {
  const val = rowData.工程時間?.value
  const reasons: string[] = Array.isArray(rowData.データ不備) ? rowData.データ不備 : [];
  const isDefault = (typeof val === 'number' && Math.abs(val - 0.001) < 1e-9 && reasons.some(r => r.includes('工程時間')))
  const display = (val !== undefined && val !== null) ? formatFloat(val, 4) : '未設定'
  return <span className={isDefault ? 'text-red-600' : ''}>{display}</span>;
  };

  const dialogHeader = (
    <div className="flex align-items-center gap-2">
      <i className="pi pi-exclamation-triangle text-orange-500"></i>
      <span>データ不備品目一覧 ({deficiencyItems.length}件)</span>
    </div>
  );

  return (
    <Dialog
      header={dialogHeader}
      footer={
        <div className="flex justify-content-end mt-4">
          <Button
            label="閉じる"
            icon="pi pi-times"
            onClick={onHide}
            className="p-button-secondary"
          />
        </div>
      }
      visible={visible}
      onHide={onHide}
      style={{ width: '80vw', maxWidth: '1200px' }}
      modal
      className="data-deficiency-dialog"
    >
      <div className="mb-3">
        <p className="text-sm text-600">
          以下の品目は在庫上限、在庫下限、工程時間のいずれかが設定されていないため、
          計画作成時に正しく処理されない可能性があります。
        </p>
      </div>

      {deficiencyItems.length === 0 ? (
        <div className="text-center py-6">
          <i className="pi pi-check-circle text-green-500 text-4xl mb-3"></i>
          <p className="text-lg">データ不備の品目はありません</p>
        </div>
      ) : (
        <DataTable
          value={deficiencyItems}
          stripedRows
          showGridlines
          paginator
          rows={20}
          rowsPerPageOptions={[10, 20, 50]}
          tableStyle={{ minWidth: '50rem' }}
          className="w-full"
          emptyMessage="データ不備の品目はありません"
        >
          <Column field="品目ID" header="品目ID" sortable style={{ minWidth: '8rem' }} />
          <Column field="品目名" header="品目名" sortable style={{ minWidth: '12rem' }} />
          <Column field="分類" header="分類" sortable style={{ minWidth: '8rem' }} />
          <Column field="製造分類" header="製造分類" sortable style={{ minWidth: '10rem' }} />
          <Column
            field="在庫上限"
            header="在庫上限"
            body={stockMaxTemplate}
            sortable
            style={{ minWidth: '8rem' }}
          />
          <Column
            field="在庫下限"
            header="在庫下限"
            body={stockMinTemplate}
            sortable
            style={{ minWidth: '8rem' }}
          />
          <Column
            field="工程時間"
            header="工程時間"
            body={processTimeTemplate}
            sortable
            style={{ minWidth: '8rem' }}
          />
          <Column
            header="不備理由"
            body={deficiencyReasonTemplate}
            style={{ minWidth: '12rem' }}
          />
        </DataTable>
      )}
    </Dialog>
  );
};

export default DataDeficiencyDialog;
